import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Plus, LogOut, Loader2, ExternalLink, Upload, Link as LinkIcon, ListPlus, TrendingUp, Users, Eye, ShoppingCart } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { key: "storage-boxes", label: "Storage Boxes" },
  { key: "display-cases", label: "Display Cases" },
  { key: "shoe-care", label: "Shoe Care" },
];
const EMPTY = { name: "", description: "", category: "storage-boxes", price: "", image_url: "", images: [], stock: "", source_url: "", source_price: "", markup_percent: "15" };

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("kg_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lowStock, setLowStock] = useState({ count: 0, products: [], threshold: 3 });
  const [analytics, setAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [tab, setTab] = useState("inventory");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [bulkUrls, setBulkUrls] = useState("");
  const [bulkCategory, setBulkCategory] = useState("storage-boxes");
  const [bulkMarkup, setBulkMarkup] = useState("15");
  const [bulkStock, setBulkStock] = useState("1");
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const scrapeFromUrl = async () => {
    const url = (form.source_url || "").trim();
    if (!url) { toast.error("Paste a source URL first"); return; }
    setScraping(true);
    try {
      const r = await axios.post(`${API}/admin/scrape`, { url }, { headers: headers() });
      const d = r.data;
      const sp = Number(d.source_price) || 0;
      const mk = parseFloat(form.markup_percent) || 15;
      setForm((f) => {
        const newImage = f.image_url || d.image_url || "";
        const newImages = (f.images && f.images.length > 0) ? f.images : (d.image_url ? [d.image_url] : []);
        return {
          ...f,
          name: f.name || d.name || "",
          description: f.description || d.description || "",
          image_url: newImage,
          images: newImages,
          source_price: sp ? String(sp) : f.source_price,
          price: sp ? (sp * (1 + mk / 100)).toFixed(2) : f.price,
        };
      });
      toast.success(d.method === "ai" ? "Imported with AI extraction" : "Product details imported");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not scrape URL");
    } finally {
      setScraping(false);
    }
  };

  const runBulkImport = async () => {
    const urls = bulkUrls.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error("Paste at least one URL"); return; }
    setBulkRunning(true);
    setBulkResults(null);
    try {
      const r = await axios.post(`${API}/admin/bulk-import`, {
        urls,
        category: bulkCategory,
        markup_percent: parseFloat(bulkMarkup) || 0,
        stock: parseInt(bulkStock || "0", 10),
      }, { headers: headers() });
      setBulkResults(r.data);
      toast.success(`${r.data.created} of ${urls.length} products imported`);
      loadData(password);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const uploadImage = async (file, target = "single") => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: headers() });
      if (target === "gallery") {
        setForm((f) => {
          const images = [...(f.images || []), r.data.url].slice(0, 6);
          return { ...f, images, image_url: f.image_url || r.data.url };
        });
      } else {
        setForm((f) => ({ ...f, image_url: r.data.url, images: (f.images && f.images.length) ? f.images : [r.data.url] }));
      }
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => {
      const images = [...(f.images || [])];
      const [removed] = images.splice(idx, 1);
      const image_url = f.image_url === removed ? (images[0] || "") : f.image_url;
      return { ...f, images, image_url };
    });
  };

  const setPrimaryImage = (idx) => {
    setForm((f) => {
      const images = [...(f.images || [])];
      if (idx <= 0 || idx >= images.length) return f;
      const [pick] = images.splice(idx, 1);
      images.unshift(pick);
      return { ...f, images, image_url: pick };
    });
  };

  const headers = useCallback(() => ({ "X-Admin-Password": password }), [password]);

  const loadData = useCallback(async (pw) => {
    const h = { "X-Admin-Password": pw };
    const [p, o, l] = await Promise.all([
      axios.get(`${API}/admin/products`, { headers: h }),
      axios.get(`${API}/admin/orders`, { headers: h }),
      axios.get(`${API}/admin/low-stock`, { headers: h }),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setLowStock(l.data);
  }, []);

  useEffect(() => {
    if (password) {
      axios.post(`${API}/admin/login`, { password })
        .then(() => { setAuthed(true); loadData(password); })
        .catch(() => { sessionStorage.removeItem("kg_admin"); setPassword(""); });
    }
  }, [password, loadData]);

  const loadAnalytics = useCallback(async (days = 30) => {
    if (!password) return;
    try {
      const r = await axios.get(`${API}/admin/analytics?days=${days}`, { headers: { "X-Admin-Password": password } });
      setAnalytics(r.data);
    } catch {
      toast.error("Could not load analytics");
    }
  }, [password]);

  useEffect(() => {
    if (authed && tab === "analytics") loadAnalytics(analyticsRange);
  }, [authed, tab, analyticsRange, loadAnalytics]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password: pwInput });
      sessionStorage.setItem("kg_admin", pwInput);
      setPassword(pwInput);
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("kg_admin");
    setPassword("");
    setAuthed(false);
  };

  const openCreate = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description, category: p.category, price: String(p.price),
      image_url: p.image_url, images: p.images || (p.image_url ? [p.image_url] : []),
      stock: String(p.stock),
      source_url: p.source_url || "", source_price: p.source_price ? String(p.source_price) : "",
      markup_percent: p.markup_percent != null ? String(p.markup_percent) : "15",
    });
    setEditId(p.id);
    setDialogOpen(true);
  };

  const setSourceField = (field, value) => {
    const next = { ...form, [field]: value };
    const sp = parseFloat(next.source_price);
    const mk = parseFloat(next.markup_percent);
    if (sp > 0 && !isNaN(mk)) next.price = (sp * (1 + mk / 100)).toFixed(2);
    setForm(next);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock: parseInt(form.stock || "0", 10),
      source_price: parseFloat(form.source_price) || 0,
      markup_percent: parseFloat(form.markup_percent) || 0,
      images: (form.images && form.images.length > 0) ? form.images : (form.image_url ? [form.image_url] : []),
    };
    try {
      if (editId) {
        await axios.put(`${API}/admin/products/${editId}`, payload, { headers: headers() });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: headers() });
        toast.success("Product added");
      }
      setDialogOpen(false);
      loadData(password);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: headers() });
      toast.success("Product deleted");
      loadData(password);
    } catch {
      toast.error("Delete failed");
    }
  };

  const fulfill = async (sessionId) => {
    try {
      await axios.put(`${API}/admin/orders/${sessionId}/fulfill`, {}, { headers: headers() });
      toast.success("Order marked fulfilled");
      loadData(password);
    } catch {
      toast.error("Could not mark fulfilled");
    }
  };

  const stats = useMemo(() => {
    const paid = orders.filter((o) => o.payment_status === "paid");
    const rev = paid.reduce((a, o) => a + (o.revenue || 0), 0);
    const cost = paid.reduce((a, o) => a + (o.cost || 0), 0);
    const profit = rev - cost;
    return {
      count: paid.length,
      revenue: rev,
      cost,
      profit,
      margin: rev > 0 ? (profit / rev) * 100 : 0,
    };
  }, [orders]);

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <form onSubmit={login} className="border border-border p-10 max-w-sm w-full" data-testid="admin-login-form">
          <h1 className="font-display font-bold text-2xl tracking-tight">ADMIN ACCESS<span className="text-primary">.</span></h1>
          <p className="text-sm text-muted-foreground mt-2">KicksGuard Supply Company</p>
          <input
            type="password"
            data-testid="admin-password-input"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Password"
            className="mt-6 w-full border border-border px-4 py-3 text-sm focus:border-foreground outline-none"
          />
          <button type="submit" data-testid="admin-login-button" className="mt-4 w-full bg-primary text-white py-3 text-sm uppercase tracking-wider hover:bg-foreground transition-colors">
            Enter
          </button>
          <Link to="/" className="block text-center text-xs text-muted-foreground mt-4 hover:text-foreground transition-colors">← Back to store</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-bold tracking-tight">KICKSGUARD<span className="text-primary">.</span>ADMIN</Link>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
        {lowStock.count > 0 && (
          <div data-testid="low-stock-banner" className="mb-6 border-l-4 border-primary bg-primary/5 p-4 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-display font-semibold text-sm uppercase tracking-widest text-primary">
                {lowStock.count} product{lowStock.count === 1 ? "" : "s"} low on stock
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {lowStock.products.slice(0, 5).map((p) => (
                  <span key={p.id} className="mr-3">
                    <span className="text-foreground font-medium">{p.name}</span>
                    <span className="text-primary"> · {p.stock} left</span>
                  </span>
                ))}
                {lowStock.count > 5 && <span className="text-muted-foreground">…and {lowStock.count - 5} more</span>}
              </p>
            </div>
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Alerts under {lowStock.threshold} units</span>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div className="flex gap-2">
            {["inventory", "bulk", "analytics", "orders"].map((t) => (
              <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
                className={`px-5 py-2 text-sm uppercase tracking-wider border transition-colors ${tab === t ? "bg-foreground text-white border-foreground" : "border-border hover:border-foreground"}`}>
                {t === "bulk" ? "Bulk Import" : t}
              </button>
            ))}
          </div>
          {tab === "inventory" && (
            <button data-testid="admin-add-product-button" onClick={openCreate} className="flex items-center gap-2 bg-primary text-white px-5 py-2 text-sm uppercase tracking-wider hover:bg-foreground transition-colors">
              <Plus size={16} /> Add Product
            </button>
          )}
        </div>

        {tab === "inventory" && (
          <div className="border border-border">
            <Table data-testid="admin-products-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No products yet. Add your first one.</TableCell></TableRow>
                )}
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.category}</TableCell>
                    <TableCell>${p.price.toFixed(2)}</TableCell>
                    <TableCell>
                      <span className={p.stock <= 3 ? "text-primary font-medium" : ""}>{p.stock}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:text-primary transition-colors"><Pencil size={15} /></button>
                      <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 hover:text-primary transition-colors"><Trash2 size={15} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "analytics" && (
          <div className="space-y-6" data-testid="admin-analytics-panel">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display font-bold text-2xl tracking-tight">TRAFFIC & CONVERSION<span className="text-primary">.</span></h2>
                <p className="text-sm text-muted-foreground mt-1">Last {analytics?.range_days || analyticsRange} days · admin activity excluded</p>
              </div>
              <div className="flex gap-2">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    data-testid={`analytics-range-${d}`}
                    onClick={() => setAnalyticsRange(d)}
                    className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${analyticsRange === d ? "bg-foreground text-white border-foreground" : "border-border hover:border-foreground"}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {!analytics ? (
              <div className="border border-border py-16 text-center text-muted-foreground text-sm uppercase tracking-widest">Loading analytics...</div>
            ) : (
              <>
                {/* KPI grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="analytics-kpi-grid">
                  {[
                    { label: "Unique visitors", value: analytics.kpis.unique_visitors, icon: Users },
                    { label: "Pageviews", value: analytics.kpis.pageviews, icon: Eye },
                    { label: "Product views", value: analytics.kpis.product_views, icon: Eye },
                    { label: "Cart adds", value: analytics.kpis.cart_adds, icon: ShoppingCart },
                    { label: "Checkouts started", value: analytics.kpis.checkout_starts, icon: ShoppingCart },
                    { label: "Paid orders", value: analytics.kpis.paid_orders, icon: TrendingUp, accent: true },
                    { label: "Revenue", value: `$${analytics.kpis.revenue.toFixed(2)}`, icon: TrendingUp, accent: true },
                    { label: "New signups", value: analytics.kpis.new_signups, icon: Users, sub: `${analytics.kpis.total_users} total members` },
                  ].map((k) => (
                    <div key={k.label} data-testid={`analytics-kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white p-5">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                        <k.icon size={13} /> {k.label}
                      </div>
                      <p className={`mt-2 font-display font-bold text-2xl tracking-tight ${k.accent ? "text-primary" : ""}`}>{k.value}</p>
                      {k.sub && <p className="text-xs text-muted-foreground mt-1">{k.sub}</p>}
                    </div>
                  ))}
                </div>

                {/* Rate row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: "Signup rate", value: `${analytics.kpis.signup_rate}%`, hint: "signups / unique visitors" },
                    { label: "Add-to-cart rate", value: `${analytics.kpis.cart_rate}%`, hint: "cart adds / product views" },
                    { label: "Conversion rate", value: `${analytics.kpis.conversion_rate}%`, hint: "paid orders / unique visitors" },
                  ].map((r) => (
                    <div key={r.label} className="border border-border p-4">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">{r.label}</p>
                      <p className="mt-1 font-display font-semibold text-xl">{r.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{r.hint}</p>
                    </div>
                  ))}
                </div>

                {/* Timeline chart (bars) */}
                <div className="border border-border p-6" data-testid="analytics-timeline">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Daily traffic</p>
                    <p className="text-xs text-muted-foreground">
                      <span className="inline-block w-2 h-2 bg-foreground mr-1 align-middle"></span>
                      Pageviews
                      <span className="inline-block w-2 h-2 bg-primary ml-4 mr-1 align-middle"></span>
                      Unique visitors
                    </p>
                  </div>
                  {(() => {
                    const max = Math.max(1, ...analytics.timeline.map((t) => t.pageviews));
                    return (
                      <div className="mt-4 flex items-end gap-1 h-40">
                        {analytics.timeline.map((t) => (
                          <div key={t.date} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${t.date} · ${t.pageviews} views · ${t.visitors} visitors`}>
                            <div className="w-full bg-foreground/80" style={{ height: `${(t.pageviews / max) * 100}%`, minHeight: t.pageviews ? "2px" : "0" }} />
                            <div className="w-full bg-primary" style={{ height: `${(t.visitors / max) * 100}%`, minHeight: t.visitors ? "2px" : "0" }} />
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
                    <span>{analytics.timeline[0]?.date}</span>
                    <span>{analytics.timeline[analytics.timeline.length - 1]?.date}</span>
                  </div>
                </div>

                {/* Funnel */}
                <div className="border border-border p-6" data-testid="analytics-funnel">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Purchase funnel</p>
                  <div className="mt-4 space-y-3">
                    {(() => {
                      const first = analytics.funnel[0]?.value || 1;
                      return analytics.funnel.map((f) => {
                        const pct = first ? Math.round((f.value / first) * 100) : 0;
                        return (
                          <div key={f.stage} className="flex items-center gap-4">
                            <span className="w-40 text-sm">{f.stage}</span>
                            <div className="flex-1 bg-secondary h-8 relative overflow-hidden">
                              <div className="h-full bg-foreground" style={{ width: `${Math.max(pct, f.value > 0 ? 2 : 0)}%` }} />
                              <span className="absolute inset-0 flex items-center px-3 text-xs font-medium text-white mix-blend-difference">{f.value} · {pct}%</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Top products */}
                <div className="border border-border" data-testid="analytics-top-products">
                  <div className="p-4 border-b border-border bg-secondary/50 flex items-center justify-between">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Most viewed products</p>
                    <p className="text-xs text-muted-foreground">Top 10</p>
                  </div>
                  {analytics.top_products.length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">No product views tracked yet.</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {analytics.top_products.map((p, idx) => (
                        <div key={p.product_id} data-testid={`top-product-${idx}`} className="p-4 flex items-center justify-between gap-4 text-sm">
                          <div className="min-w-0 flex-1 flex items-center gap-3">
                            <span className="font-display font-semibold text-muted-foreground w-6">{idx + 1}</span>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{p.name}</p>
                              <p className="text-xs text-muted-foreground">${(p.price || 0).toFixed(2)} · {p.stock} in stock · {p.category}</p>
                            </div>
                          </div>
                          <span className="shrink-0 text-xs uppercase tracking-wider px-2 py-1 bg-foreground text-white">{p.views} views</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "bulk" && (
          <div className="space-y-6" data-testid="admin-bulk-panel">
            <div className="border border-border p-6">
              <h2 className="font-display font-bold text-xl tracking-tight">BULK IMPORT FROM URLS<span className="text-primary">.</span></h2>
              <p className="text-sm text-muted-foreground mt-2">Paste one product URL per line. We pull the name, description, image and source price, then apply your markup automatically.</p>
              <textarea
                data-testid="bulk-urls-input"
                value={bulkUrls}
                onChange={(e) => setBulkUrls(e.target.value)}
                placeholder={"https://example.com/product-a\nhttps://example.com/product-b"}
                rows={8}
                className="mt-4 w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground font-mono resize-y"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Category</span>
                  <select data-testid="bulk-category" value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} className="w-full border border-border px-4 py-3 text-sm outline-none bg-white">
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Markup %</span>
                  <input data-testid="bulk-markup" type="number" step="0.1" min="0" value={bulkMarkup} onChange={(e) => setBulkMarkup(e.target.value)} className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Starting stock</span>
                  <input data-testid="bulk-stock" type="number" min="0" value={bulkStock} onChange={(e) => setBulkStock(e.target.value)} className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground" />
                </label>
              </div>
              <button
                data-testid="bulk-import-run"
                onClick={runBulkImport}
                disabled={bulkRunning}
                className="mt-5 inline-flex items-center gap-2 bg-primary text-white px-6 py-3 text-sm uppercase tracking-wider hover:bg-foreground transition-colors disabled:opacity-60"
              >
                {bulkRunning ? <Loader2 size={16} className="animate-spin" /> : <ListPlus size={16} />}
                {bulkRunning ? "Importing..." : "Import All"}
              </button>
            </div>
            {bulkResults && (
              <div className="border border-border" data-testid="bulk-results">
                <div className="p-4 border-b border-border bg-secondary/50 text-sm">
                  <span className="font-display font-semibold">{bulkResults.created}</span> of {bulkResults.results.length} imported
                </div>
                <div className="divide-y divide-border">
                  {bulkResults.results.map((r, i) => (
                    <div key={i} data-testid={`bulk-result-${i}`} className="p-4 flex items-start justify-between gap-4 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-muted-foreground text-xs">{r.url}</p>
                        {r.ok ? (
                          <p className="mt-1 font-medium">{r.name} <span className="text-muted-foreground">— sale ${r.price?.toFixed(2)}</span>{r.method === "ai" && <span className="ml-2 text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5">AI</span>}</p>
                        ) : (
                          <p className="mt-1 text-primary">{r.error}</p>
                        )}
                      </div>
                      <span className={`shrink-0 text-xs uppercase tracking-wider px-2 py-1 ${r.ok ? "bg-foreground text-white" : "bg-primary/10 text-primary"}`}>
                        {r.ok ? "Added" : "Skipped"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="admin-orders-list">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="admin-profit-summary">
              {[
                { label: "Orders paid", value: stats.count, prefix: "" },
                { label: "Revenue", value: stats.revenue.toFixed(2), prefix: "$" },
                { label: "Source cost", value: stats.cost.toFixed(2), prefix: "$" },
                { label: "Profit", value: stats.profit.toFixed(2), prefix: "$", accent: true, suffix: ` (${stats.margin.toFixed(1)}%)` },
              ].map((s) => (
                <div key={s.label} className="bg-white p-5">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</p>
                  <p className={`mt-2 font-display font-bold text-2xl tracking-tight ${s.accent ? "text-primary" : ""}`}>
                    {s.prefix}{s.value}{s.suffix ? <span className="text-sm font-medium text-muted-foreground ml-1">{s.suffix}</span> : null}
                  </p>
                </div>
              ))}
            </div>
            {orders.length === 0 && (
              <div className="border border-border py-16 text-center text-muted-foreground">No orders yet.</div>
            )}
            {orders.map((o) => (
              <div key={o.session_id} data-testid={`admin-order-${o.session_id}`} className="border border-border">
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border bg-secondary/50">
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</span>
                    <span className="font-display font-semibold">${((o.amount_total ?? o.amount) / 100).toFixed(2)}</span>
                    <span className={`text-xs uppercase tracking-wider px-2 py-1 ${o.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-secondary text-muted-foreground"}`}>
                      {o.payment_status}
                    </span>
                    {o.payment_status === "paid" && (
                      <>
                        <span data-testid={`fulfillment-badge-${o.session_id}`} className={`text-xs uppercase tracking-wider px-2 py-1 ${o.fulfillment_status === "fulfilled" ? "bg-foreground text-white" : "bg-primary/10 text-primary"}`}>
                          {o.fulfillment_status === "fulfilled" ? "Fulfilled" : "Needs Fulfillment"}
                        </span>
                        <span data-testid={`order-profit-${o.session_id}`} className="text-xs uppercase tracking-wider px-2 py-1 bg-secondary text-foreground">
                          Profit ${Number(o.profit || 0).toFixed(2)} · {Number(o.margin_percent || 0).toFixed(1)}%
                        </span>
                      </>
                    )}
                  </div>
                  {o.payment_status === "paid" && o.fulfillment_status !== "fulfilled" && (
                    <button data-testid={`fulfill-button-${o.session_id}`} onClick={() => fulfill(o.session_id)} className="bg-foreground text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-primary transition-colors">
                      Mark Fulfilled
                    </button>
                  )}
                </div>
                <div className="p-4 grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Items</p>
                    <div className="space-y-2">
                      {(o.items || []).map((i, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3 text-sm">
                          <span>{i.name} ×{i.quantity} <span className="text-muted-foreground">(${i.unit_price?.toFixed(2)})</span></span>
                          {i.source_url && (
                            <a
                              data-testid={`order-source-link-${o.session_id}-${idx}`}
                              href={i.source_url} target="_blank" rel="noopener noreferrer"
                              className="shrink-0 inline-flex items-center gap-1 bg-primary text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-foreground transition-colors"
                            >
                              Order from Source <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Ship To</p>
                    {o.shipping?.address ? (
                      <div data-testid={`order-shipping-${o.session_id}`} className="text-sm leading-relaxed">
                        <p className="font-medium">{o.shipping.name || o.customer_name}</p>
                        <p>{o.shipping.address.line1}{o.shipping.address.line2 ? `, ${o.shipping.address.line2}` : ""}</p>
                        <p>{o.shipping.address.city}, {o.shipping.address.state} {o.shipping.address.postal_code}</p>
                        <p>{o.shipping.address.country}</p>
                        {o.customer_email && <p className="text-muted-foreground mt-1">{o.customer_email}</p>}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{o.customer_email || "Awaiting payment — no address yet"}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editId ? "EDIT PRODUCT" : "ADD PRODUCT"}</DialogTitle>
            <DialogDescription>Products sync to Stripe automatically on save.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <input data-testid="product-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground" />
            <textarea data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-foreground resize-none" />
            <div className="grid grid-cols-2 gap-4">
              <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-border px-4 py-3 text-sm outline-none bg-white">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <input data-testid="product-form-price" required type="number" step="0.01" min="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price (USD)" className="border border-border px-4 py-3 text-sm outline-none focus:border-foreground" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="product-form-stock" required type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock qty" className="border border-border px-4 py-3 text-sm outline-none focus:border-foreground" />
              <input data-testid="product-form-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value, images: e.target.value ? [e.target.value, ...(form.images || []).filter((i) => i !== e.target.value).slice(0, 5)] : (form.images || []) })} placeholder="Primary image URL" className="border border-border px-4 py-3 text-sm outline-none focus:border-foreground" />
            </div>
            <div className="border border-border p-4 space-y-3" data-testid="product-form-gallery">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Image gallery ({(form.images || []).length}/6)</p>
                <label data-testid="product-form-upload-button" className={`inline-flex items-center gap-2 border border-foreground px-3 py-2 text-xs uppercase tracking-wider cursor-pointer hover:bg-foreground hover:text-white transition-colors ${uploading || (form.images || []).length >= 6 ? "opacity-60 pointer-events-none" : ""}`}>
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? "Uploading..." : "Add Image"}
                  <input data-testid="product-form-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => { uploadImage(e.target.files?.[0], "gallery"); e.target.value = ""; }} />
                </label>
              </div>
              {(form.images || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">Add up to 6 photos so buyers can see every angle. The first image is the thumbnail.</p>
              ) : (
                <div className="grid grid-cols-6 gap-2">
                  {(form.images || []).map((src, i) => (
                    <div key={`${src}-${i}`} data-testid={`gallery-image-${i}`} className="relative group aspect-square border border-border overflow-hidden">
                      <img src={src} alt={`Product ${i + 1}`} className="w-full h-full object-cover" />
                      {i === 0 && <span className="absolute top-1 left-1 bg-foreground text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider">Primary</span>}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                        {i !== 0 && (
                          <button type="button" data-testid={`gallery-set-primary-${i}`} onClick={() => setPrimaryImage(i)} className="text-white text-[10px] uppercase tracking-wider hover:text-primary">Set primary</button>
                        )}
                        <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => removeGalleryImage(i)} className="text-white text-[10px] uppercase tracking-wider hover:text-primary">Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border border-border p-4 space-y-3 bg-secondary/60">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Dropship source — never shown to customers</p>
              <div className="flex gap-2">
                <input data-testid="product-form-source-url" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="Source URL (e.g. Amazon link)" className="flex-1 border border-border px-4 py-3 text-sm outline-none focus:border-foreground bg-white" />
                <button
                  type="button"
                  data-testid="product-form-scrape-button"
                  onClick={scrapeFromUrl}
                  disabled={scraping || !form.source_url}
                  className="shrink-0 inline-flex items-center gap-2 border border-foreground px-3 py-2 text-xs uppercase tracking-wider hover:bg-foreground hover:text-white transition-colors disabled:opacity-50"
                >
                  {scraping ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />}
                  {scraping ? "Fetching" : "Import"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="product-form-source-price" type="number" step="0.01" min="0" value={form.source_price} onChange={(e) => setSourceField("source_price", e.target.value)} placeholder="Source price (USD)" className="border border-border px-4 py-3 text-sm outline-none focus:border-foreground bg-white" />
                <input data-testid="product-form-markup" type="number" step="0.1" min="0" value={form.markup_percent} onChange={(e) => setSourceField("markup_percent", e.target.value)} placeholder="Markup %" className="border border-border px-4 py-3 text-sm outline-none focus:border-foreground bg-white" />
              </div>
              <p className="text-xs text-muted-foreground">Entering a source price auto-fills the sale price at your markup — you can still edit the price above.</p>
            </div>
            <button type="submit" data-testid="product-form-submit" disabled={saving} className="w-full bg-primary text-white py-3 text-sm uppercase tracking-wider hover:bg-foreground transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? "Saving..." : editId ? "Update Product" : "Add Product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
